import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { useOrganization } from "@clerk/clerk-react";
import { Sidebar } from "./Sidebar";
import { WorkspaceProvider } from "@/lib/workspace-context";

/**
 * Map the current route to the design's `view-*` class so the design's CSS
 * collapses the left rail / right rail columns appropriately. Without this
 * the shell stays in 3-column mode and Kanban / Calendar render in a thin
 * strip in the middle.
 */
function viewClass(pathname: string): string {
  // The CSS class is `view-article` because the design system's stylesheet
  // keys layout off that token. Keeping it stable avoids a sweeping CSS
  // rename — the rename in S-086 covers vocabulary, not cosmetic class names.
  if (pathname.startsWith("/piece/") || pathname.startsWith("/article/"))
    return "view-article";
  if (pathname.startsWith("/calendar")) return "view-calendar";
  if (pathname.startsWith("/settings/")) return "view-settings";
  return "view-kanban";
}

/**
 * Shell — top-level chrome around the routed views.
 *
 * The legacy global "+ New article" button (handled here via the
 * legacy CreateArticleModal) was removed in Phase 9 / S-071 because:
 *   1. The legacy modal queried Directus (`@/lib/queries`) to populate
 *      the customer dropdown; Directus was removed from the bundle in
 *      S-073.
 *   2. The Hono-backed KanbanView (S-071) and CalendarView (S-085)
 *      ship a shared CreatePieceModal scoped to the active workspace.
 *   3. Settings views don't need a global "new piece" affordance.
 *
 * BUG-068: wraps the routed views in one `WorkspaceProvider` so the
 * workspace selected in Kanban/Calendar/Social's own picker is the same
 * piece of state everywhere, instead of each view tracking it separately.
 *
 * FEAT-087 (Phase 1): the horizontal `TopBar` is replaced by the persistent
 * left-rail `Sidebar`. The provider now wraps the sidebar as well as the
 * routed views, because the sidebar owns the workspace picker and reads the
 * same context. Onboarding renders without the rail — per the design, the
 * sidebar is suppressed there (and on Sign in, which already sits outside
 * this Shell route entirely).
 */
export function Shell() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { organization, membership } = useOrganization();
  const isAdmin = !!organization && membership?.role === "org:admin";
  const showSidebar = !pathname.startsWith("/onboarding");
  return (
    <WorkspaceProvider>
      <div className={`app panel-overlay ${viewClass(pathname)}`}>
        {showSidebar && (
          <Sidebar onOpenSettings={isAdmin ? () => navigate("/settings") : undefined} />
        )}
        <div className="shell">
          <Outlet />
        </div>
      </div>
    </WorkspaceProvider>
  );
}
